'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { MenuIcon, CloseIcon } from '@/components/ui/icons';

/**
 * Accessible mobile navigation drawer.
 * Uses a focus trap and Escape-to-close.
 * @param {{ navItems: Array<{ label: string, href: string }> }} props
 */
export function MobileMenu({ navItems }) {
  const [open, setOpen] = useState(false);
  const drawerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const firstLink = drawerRef.current?.querySelector('a');
    firstLink?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open navigation menu"
        aria-expanded={open}
        className="inline-flex items-center justify-center rounded-md p-2 text-text-secondary hover:bg-surface-sunken md:hidden"
      >
        <MenuIcon className="h-6 w-6" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="fixed inset-0 bg-black/50"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="fixed inset-y-0 right-0 w-full max-w-xs bg-surface shadow-lg"
          >
            <div className="flex h-16 items-center justify-between border-b border-border px-4">
              <span className="text-sm font-semibold text-text-primary">Menu</span>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
                aria-label="Close navigation menu"
                className="rounded-md p-2 text-text-secondary hover:bg-surface-sunken"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <nav aria-label="Mobile" className="flex flex-col p-4">
              {navItems.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="rounded-md px-3 py-3 text-base font-medium text-text-primary hover:bg-surface-sunken"
                >
                  {link.label}
                </Link>
              ))}
              <hr className="my-3 border-border" />
              <Link
                href="/auth/login"
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-base font-medium text-text-secondary hover:bg-surface-sunken"
              >
                Sign in
              </Link>
              <Link
                href="/auth/register"
                onClick={() => setOpen(false)}
                className="mt-1 rounded-md bg-brand-600 px-3 py-3 text-center text-base font-medium text-white hover:bg-brand-700"
              >
                Register
              </Link>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}